import { UserAddOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Modal } from 'antd'
import classNames from 'classnames'
import { Suspense, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import SortableColumnTitle from '@/components/SortableColumnTitle/SortableColumnTitle'
import type { UserInterface } from '@/types'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { safeLazy } from '@/utils/app/safeLazy'

import { usersQueries } from './api/usersQueries'
import { useUpdateUserMutation } from './hooks/useUpdateUserMutation'
import { useUsersAccess } from './hooks/useUsersAccess'
import { useUsersList } from './hooks/useUsersList'
import {
  hasRolesChanged,
  isUserFormUnchanged,
  toUpdateUserRequest,
  toUserEditFormValues,
} from './lib/userEditForm'
import {
  getRolesChangedModalConfig,
  userEditFormSchema,
  type UserEditFormType,
} from './Users.config'
import cn from './Users.module.scss'
import {
  getUsersColumns,
  type RenderSortTitle,
  USERS_COLUMN_WIDTHS,
} from './usersColumns'
import UsersFilters from './UsersFilters/UsersFilters'

const UserCreationModal = safeLazy(
  () =>
    import(
      '@/components/EntityCreationModals/UserCreationModal/UserCreationModal'
    )
)

function Users() {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserEditFormType>({
    shouldFocusError: false,
    resolver: zodResolver(userEditFormSchema),
  })
  const [open, setOpen] = useState<boolean>(false)
  const [editingKey, setEditingKey] = useState('')
  const [modal, contextHolder] = Modal.useModal()
  const queryClient = useQueryClient()
  const updateUser = useUpdateUserMutation()
  const watchedFormValues = watch()

  const {
    organisationCode,
    isSelectedOrganisation,
    canManageUsers,
    roleOptions,
    canEditUser,
  } = useUsersAccess()

  const { filters, setFilters, getSortOrder, toggleSort, usersQuery } =
    useUsersList(organisationCode)
  const users = usersQuery.data ?? []

  const edit = (record: UserInterface) => {
    reset(toUserEditFormValues(record, roleOptions))
    setEditingKey(record.id)
  }

  const cancel = () => {
    setEditingKey('')
    reset()
  }

  const editingUser = users.find((user) => user.id === editingKey)
  const isSaveDisabled =
    !editingUser || isUserFormUnchanged(watchedFormValues, editingUser)

  const submitUser = (user: UserInterface, values: UserEditFormType) => {
    if (updateUser.isPending) return
    const payload = toUpdateUserRequest(values, user, canManageUsers)
    updateUser.mutate(
      { id: user.id, payload },
      {
        onSuccess: () => {
          setEditingKey('')
          reset()
          toast.success('Данные обновлены')
          if (hasRolesChanged(payload, user))
            modal.info(getRolesChangedModalConfig())
        },
        onError: (error) => handleApiError(error, modal),
      }
    )
  }

  const save = (id: string) => {
    const user = users.find((item) => item.id === id)
    if (user) handleSubmit((values) => submitUser(user, values))()
  }

  const renderSortTitle: RenderSortTitle = (field, title) => (
    <SortableColumnTitle
      title={title}
      order={getSortOrder(field)}
      onToggle={() => toggleSort(field)}
      isOrganisationSelected={isSelectedOrganisation}
    />
  )

  const columns = getUsersColumns({
    editingKey,
    canManageUsers,
    isSaveDisabled,
    editForm: {
      control,
      register,
      errors,
      roleOptions,
      rolesPopupClassName: isSelectedOrganisation
        ? cn.rolesPopupSelected
        : cn.rolesPopup,
      isOrganisationSelected: isSelectedOrganisation,
    },
    canEditUser,
    renderSortTitle,
    onEdit: edit,
    onSave: save,
    onCancel: cancel,
  })

  const renderTable = () => {
    if (usersQuery.isPending) return <Loader paddingTop={0} />
    if (usersQuery.isError)
      return (
        <p className={cn.emptyUsersTableMessage}>
          {getGeneralErrorMessage(usersQuery.error)}{' '}
          <Button type="link" onClick={() => usersQuery.refetch()}>
            Повторить
          </Button>
        </p>
      )
    return (
      <AtomTable
        ariaLabel="Пользователи"
        data={users}
        columns={columns}
        getRowId={(user) => user.id}
        columnWidths={USERS_COLUMN_WIDTHS}
        isOrganisationSelected={isSelectedOrganisation}
        isStale={usersQuery.isPlaceholderData}
      />
    )
  }

  return (
    <>
      {contextHolder}
      <section
        className={classNames(
          cn.users,
          isSelectedOrganisation && cn.selectedOrganisation
        )}
      >
        <div className={cn.inner}>
          {canManageUsers && (
            <div className={cn.userCreationButtonContainer}>
              <button onClick={() => setOpen(true)}>
                <div>
                  <UserAddOutlined />
                </div>
                Создать пользователя
              </button>
            </div>
          )}
          {open && (
            <Suspense fallback={null}>
              <UserCreationModal
                open={open}
                setOpen={setOpen}
                onSuccess={() =>
                  queryClient.invalidateQueries({
                    queryKey: usersQueries.lists(),
                  })
                }
              />
            </Suspense>
          )}
          <UsersFilters
            value={filters}
            onChange={setFilters}
            isOrganisationSelected={isSelectedOrganisation}
          />
          <div className={cn.tableContainer}>{renderTable()}</div>
        </div>
      </section>
    </>
  )
}

export default Users
